import { Box } from "@outschool/backpack";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import {
  CategoryPageVisitHistory,
  ClassPageVisitHistory
} from "@outschool/ui-components-website";
import {
  Container,
  Loading,
  TrackingPageSection
} from "@outschool/ui-legacy-component-library";
import { scrollToTop } from "@outschool/ui-utils";
import React, { useState } from "react";

import {
  LearnerInterestsAndRecommendedCategoriesQuery,
  LearnerInterestsAndRecommendedCategoriesQueryVariables
} from "../../../shared/generated/graphql";
import { LearnerInterestsAndRecommendedCategories } from "../../hooks/Topics";
import { LowEsaCreditBanner } from "../esaMemberships/LowEsaCreditBanner";
import { AddLearnerBanner } from "../loggedInHome/learnerCards/AddLearnerBanner";
import { LoggedInHomeMyClassesList } from "../loggedInHome/LoggedInHomeMyClassesList";
import FavoritesTab from "./tabs/FavoritesTab";
import LearnerTab from "./tabs/LearnerTab";
import RecentTab from "./tabs/RecentTab";
import StickyHeader from "./tabs/StickyHeader";
import TabHeader from "./tabs/TabHeader";
import TrendingTab from "./tabs/TrendingTab";
import { TabNames, TabValue } from "./types";
import { useParentDashboardSeed } from "./utils";

export default function ParentDashboardMobile({ children }: $TSFixMe) {
  const seed = useParentDashboardSeed();
  const topNode = React.useRef<HTMLElement>(null);

  const [tabs, setTabs] = useState<TabValue[]>([]);
  const [currentTab, setCurrentTab] = useState<TabValue | null>(null);
  const { currentUser } = useSession();

  const recentlyViewedCategories = CategoryPageVisitHistory.getList();
  const recentlyViewedActivities = ClassPageVisitHistory.getList();

  const { data, loading } = useQueryWithPreviousData<
    LearnerInterestsAndRecommendedCategoriesQuery,
    LearnerInterestsAndRecommendedCategoriesQueryVariables
  >(LearnerInterestsAndRecommendedCategories);

  React.useEffect(() => {
    if (!data?.currentUser) {
      return; // not ready
    }
    if (tabs.length > 0) {
      return; // already done
    }
    const tabsToShow: TabValue[] = [];

    // show a tab for each learner if user has learners
    const learners = data.currentUser.children.filter(
      // @ts-ignore TS(2531): Object is possibly 'null'.
      c => c.age <= 18 && c.age >= 3
    );
    if (learners?.length) {
      tabsToShow.push(
        ...(learners
          .map(learner => {
            if (learner.name) {
              return {
                name: learner.name,
                learnerUid: learner.uid
              };
            }
            return null;
          })
          .filter(Boolean) as TabValue[])
      );
    }
    // only show Favorites tab if user has saved activities or followed users
    const followedUsers = data.currentUser.followedUsers;
    const savedActivities = data.currentUser.savedActivities;
    if (followedUsers?.length || savedActivities?.length) {
      tabsToShow.push({ name: TabNames.Favorites, learnerUid: null });
    }
    // only show Recent tab if user has 1+ recently viewed activity or category
    if (recentlyViewedActivities?.length || recentlyViewedCategories?.length) {
      tabsToShow.push({ name: TabNames.Recent, learnerUid: null });
    }
    // show Trending tab always
    tabsToShow.push({ name: TabNames.Trending, learnerUid: null });

    setTabs(tabsToShow);
    setCurrentTab(tabsToShow[0]);
  }, [
    data,
    tabs,
    recentlyViewedActivities?.length,
    recentlyViewedCategories?.length,
    setTabs,
    setCurrentTab
  ]);

  const handleTabClick = (newTab: $TSFixMe) => {
    scrollToTop();
    setCurrentTab(newTab);
  };

  const currentLearner = data?.currentUser?.children
    ?.filter(learner => learner.uid === currentTab?.learnerUid)
    .shift();

  return loading ? (
    <Loading />
  ) : (
    <>
      <StickyHeader watchNode={topNode}>
        <TabHeader
          tabs={tabs}
          currentTab={currentTab}
          handleTabClick={handleTabClick}
          /* eslint-disable-next-line i18next/no-literal-string */
          keyPrefix={"sticky"}
        />
      </StickyHeader>
      <Box ref={topNode} />
      <AddLearnerBanner onLearnerClick={handleTabClick} />
      {currentUser?.hasEsaMembership && <LowEsaCreditBanner />}
      <Container sx={{ marginY: "24px" }}>
        {currentTab?.learnerUid && (
          <Box
            sx={{
              marginBottom: "24px"
            }}
          >
            <LoggedInHomeMyClassesList
              filterByLearnerUid={currentTab.learnerUid}
            />
          </Box>
        )}
        <TrackingPageSection
          name={"parent_dashboard_tab"}
          // @ts-ignore TS(2322): Type 'null' is not assignable to type 'string'.
          uniqueId={null}
          // @ts-ignore TS(2322): Type 'null' is not assignable to type 'string | nu... Remove this comment to see the full error message
          sharedProperties={{ currentTab }}
        >
          {setImpressionNode => (
            <Box ref={setImpressionNode}>
              {(() => {
                switch (currentTab?.name) {
                  /* eslint-disable-next-line i18next/no-literal-string */
                  case "Favorites":
                    return (
                      <FavoritesTab
                        // @ts-ignore TS(2532): Object is possibly 'undefined'.
                        savedActivities={data.currentUser.savedActivities}
                        // @ts-ignore TS(2532): Object is possibly 'undefined'.
                        followedUsers={data.currentUser.followedUsers}
                        // @ts-ignore TS(2532): Object is possibly 'undefined'.
                        learnerAges={data.currentUser.children.map(l => l.age)}
                        random={seed}
                      />
                    );
                  /* eslint-disable-next-line i18next/no-literal-string */
                  case "Recent":
                    return (
                      <RecentTab
                        recentlyViewedCategories={recentlyViewedCategories}
                        recentlyViewedActivities={recentlyViewedActivities}
                        random={seed}
                      />
                    );
                  /* eslint-disable-next-line i18next/no-literal-string */
                  case "Trending":
                    return (
                      <TrendingTab
                        // @ts-ignore TS(2532): Object is possibly 'undefined'.
                        learners={data.currentUser.children}
                        random={seed}
                      />
                    );
                  default:
                    return loading ? (
                      <Loading />
                    ) : (
                      <LearnerTab learner={currentLearner} />
                    );
                }
              })()}
            </Box>
          )}
        </TrackingPageSection>
        {currentTab?.name == "Trending" ? (
          <TrackingPageSection name="parent dashboard cards" uniqueId="banner">
            {setImpressionNode => (
              <Box
                ref={setImpressionNode}
                key="banner"
                sx={{
                  marginBottom: "24px",
                  marginY: "1em"
                }}
              >
                {children}
              </Box>
            )}
          </TrackingPageSection>
        ) : null}
      </Container>
    </>
  );
}
